// Operator mirror of the RealityAgent editable-knob registry
// (RealityAgent/shared/config/registry.go). Keep the two in sync — same
// section/key/type/label. The agent honors ONLY these paths from
// operations.agent_config; anything else in a row is ignored, so adding a knob
// here without adding it to the Go registry means the agent will ignore it.

export type KnobType = 'bool' | 'int' | 'string';

export interface Knob {
  section: string;
  key: string;
  type: KnobType;
  label: string;
  description: string;
  danger?: boolean; // changing this can take a PC offline — UI warns
  options?: string[]; // string enums (rendered as a select)
}

export interface KnobSection {
  section: string;
  title: string;
  knobs: Knob[];
}

export const path = (k: Pick<Knob, 'section' | 'key'>): string => `${k.section}.${k.key}`;

export const AGENT_CONFIG_SCHEMA: KnobSection[] = [
  {
    section: 'monitor',
    title: 'Sync cadence',
    knobs: [
      { section: 'monitor', key: 'check_interval_seconds', type: 'int', label: 'Sync check interval (s)', description: 'How often the file-sync engine checks for changes. Default 300.' },
      { section: 'network', key: 'enable_s3_fallback', type: 'bool', label: 'Allow S3 fallback', description: 'Secondaries may fall back to S3 when the primary is unreachable.' },
    ],
  },
  {
    section: 'mp_shadow',
    title: 'Headset shadow (multiplayer stand-in)',
    knobs: [
      { section: 'mp_shadow', key: 'enabled', type: 'bool', label: 'Headset shadow', description: 'Stream the headset pose to the multiplayer server as a stand-in for this seat; shown to the others only while the game itself is silent (crash, freeze, no multiplayer). Default OFF; enable per room once verified.' },
      { section: 'mp_shadow', key: 'hz', type: 'int', label: 'Rate (Hz)', description: 'Pose frames per second requested from the headset. Default 30, max 60.' },
      { section: 'mp_shadow', key: 'z_offset_cm', type: 'int', label: 'Floor offset (cm)', description: 'Height of the headset tracking origin above the floor. Default 83 (DEV1).' },
      { section: 'mp_shadow', key: 'offhead_stop_seconds', type: 'int', label: 'Off-head stop (s)', description: 'The shadow streams whenever the headset is on a head and stops once it has been off a head this long. 0 = always send. Default 10.' },
    ],
  },
  {
    section: 'player_paths',
    title: 'Looppaden opnemen',
    knobs: [
      { section: 'player_paths', key: 'enabled', type: 'bool', label: 'Looppaden opnemen', description: 'Record where each player walked during a run: the agent asks the multiplayer server for this PC’s own seat and stores the path in the database, tagged with the run, experience and scene. Feeds heatmaps, crowding and boundary analysis later, and keeps enough detail to replay a run. Needs RealityServer 1.3.7 or newer. Default OFF; enable per room.' },
      { section: 'player_paths', key: 'hz', type: 'int', label: 'Metingen per seconde', description: 'How often the pose is stored. Default 30, which is the server’s own rate — asking for more only records duplicates. Lower costs proportionally less storage; below about 10 a head turn stops being recognisable.' },
      { section: 'player_paths', key: 'chunk_seconds', type: 'int', label: 'Seconden per rij', description: 'How much path goes into one database row. Default 60. Lower means more rows and a smaller loss if a PC dies mid-minute; the maximum is 65.' },
      { section: 'player_paths', key: 'poll_timeout_ms', type: 'int', label: 'Wachttijd per meting (ms)', description: 'How long to wait for the server’s answer before giving up on that sample. Default 25; the whole budget at 30 Hz is 33 ms. Raise only if the multiplayer server is not on the room LAN.' },
    ],
  },
  {
    section: 'hmd_power',
    title: 'Headset aan/uit met de pc',
    knobs: [
      { section: 'hmd_power', key: 'off_with_pc', type: 'bool', label: 'Bril uit met de pc', description: 'When this PC is shut down (the dagafsluiting shuts down every PC in the room), also power off the headset paired with it, so "VR brillen uitzetten" actually happens instead of being trusted to the checklist. A PC RESTART always reboots the headset regardless of this knob. Careful: a headset that is off cannot be powered on remotely, so someone has to press the button in the morning. Default on.' },
    ],
  },
  {
    section: 'hmd_provision',
    title: 'Headset provisioning',
    knobs: [
      { section: 'hmd_provision', key: 'auto', type: 'bool', label: 'Rechten automatisch', description: 'Grant the headset agent its all-files permission automatically (wireless adb on, reboot, appops, off) when a held headset lacks it and is off a head and idle. Default on.' },
      { section: 'hmd_provision', key: 'psensor_off', type: 'bool', label: 'Negeer draagsensor (PUI)', description: 'Force SFS_PSENSOR off (undocumented PICO switch, needs a reboot). Only for rooms whose headsets pause or blank when taken off; with sleep timers at never they do not. Own worn detection keeps working. Default off.' },
    ],
  },
  {
    section: 'hmd_ipd',
    title: 'Headset IPD',
    knobs: [
      { section: 'hmd_ipd', key: 'enforce', type: 'bool', label: 'IPD vasthouden', description: 'Keep the paired headset at the IPD below by re-sending it every interval (the headset cannot report its IPD, so this is open-loop). The "Kind" flag in the dispatch pop-up suspends it for that run so the buttons on the headset can go down to 58 mm. Default on.' },
      { section: 'hmd_ipd', key: 'mm', type: 'int', label: 'IPD (mm)', description: 'The IPD every visitor gets, 62..72 (the software setter accepts nothing lower). Default 62.' },
      { section: 'hmd_ipd', key: 'interval_seconds', type: 'int', label: 'Interval (s)', description: 'How often the value is re-sent. Default 30.' },
    ],
  },
  {
    section: 'hmd_boundary',
    title: 'Headset speelgrens',
    knobs: [
      { section: 'hmd_boundary', key: 'auto_rescan', type: 'bool', label: 'Automatisch herscannen', description: 'When the headset reports its fence is gone while worn, force one re-scan (the software triple tap) before warning the operator. Default on.' },
      { section: 'hmd_boundary', key: 'rescan_after_seconds', type: 'int', label: 'Herscan na (s)', description: 'How long the fence may be missing (headset worn) before the re-scan. Default 5.' },
      { section: 'hmd_boundary', key: 'warn_after_seconds', type: 'int', label: 'Waarschuw na (s)', description: 'How long to wait after the re-scan before the "Speelgrens ontbreekt" warning. Default 20.' },
    ],
  },
  {
    section: 'hmd_shell',
    title: 'Headset shell',
    knobs: [
      { section: 'hmd_shell', key: 'auto_update', type: 'bool', label: 'Automatisch bijwerken', description: 'Update connected headsets to the room\'s desired Headset shell version (Versions tab): the PC serves the APK, the shell installs it and relaunches. Off = this PC ignores the room target. Default on.' },
    ],
  },
  {
    section: 'freeze_detector',
    title: 'Freeze detector',
    knobs: [
      { section: 'freeze_detector', key: 'enabled', type: 'bool', label: 'Freeze detector', description: 'Detect the PICO frozen-HMD failure by tailing the streaming logs.' },
      { section: 'freeze_detector', key: 'match_threshold', type: 'int', label: 'Match threshold', description: 'Matching log lines within the window required to trigger. Default 3.' },
      { section: 'freeze_detector', key: 'window_ms', type: 'int', label: 'Window (ms)', description: 'Sliding window for the freeze threshold. Default 1000.' },
      { section: 'freeze_detector', key: 'clear_seconds', type: 'int', label: 'Clear (s)', description: 'No-match gap that ends an episode and re-arms. Default 10.' },
      { section: 'freeze_detector', key: 'poll_interval_ms', type: 'int', label: 'Poll (ms)', description: 'Log poll cadence. Default 250.' },
      { section: 'freeze_detector', key: 'auto_recover', type: 'bool', label: 'Auto-recover', description: 'On freeze, restart SteamVR + Business Streaming and relaunch. Off by default — intrusive.', danger: true },
    ],
  },
  {
    section: 'stream_health',
    title: 'Stream health (no VR stream)',
    knobs: [
      { section: 'stream_health', key: 'enabled', type: 'bool', label: 'Stream-health detector', description: "Detect 'in an experience but no VR stream'." },
      { section: 'stream_health', key: 'grace_seconds', type: 'int', label: 'Grace (s)', description: 'Wait this long after launch before judging. Default 75.' },
      { section: 'stream_health', key: 'miss_threshold', type: 'int', label: 'Miss threshold', description: 'Consecutive no-stream polls after grace before firing. Default 3.' },
      { section: 'stream_health', key: 'poll_seconds', type: 'int', label: 'Poll (s)', description: 'Evaluation cadence. Default 10.' },
    ],
  },
  {
    section: 'gpu_health',
    title: 'GPU health (hung GPU / TDR)',
    knobs: [
      { section: 'gpu_health', key: 'enabled', type: 'bool', label: 'GPU-hang detector', description: 'Detect a hung/crashed GPU driver (DXGI device reset / TDR).' },
      { section: 'gpu_health', key: 'grace_seconds', type: 'int', label: 'Grace (s)', description: 'Wait this long after launch before judging. Default 90.' },
      { section: 'gpu_health', key: 'miss_threshold', type: 'int', label: 'Miss threshold', description: 'Consecutive GPU-unreadable polls before firing. Default 6.' },
      { section: 'gpu_health', key: 'poll_seconds', type: 'int', label: 'Poll (s)', description: 'Evaluation cadence. Default 10.' },
    ],
  },
  {
    section: 'stream_watchdog',
    title: 'Business Streaming watchdog',
    knobs: [
      { section: 'stream_watchdog', key: 'enabled', type: 'bool', label: 'BS idle watchdog', description: 'Periodically restart Business Streaming if it dies while the PC is idle (no experience running).' },
      { section: 'stream_watchdog', key: 'interval_seconds', type: 'int', label: 'Check interval (s)', description: 'How often to check that Business Streaming is up while idle. Default 60; floored at 15.' },
    ],
  },
  {
    section: 'display_health',
    title: 'Display health (Parsec stuck display)',
    knobs: [
      { section: 'display_health', key: 'enabled', type: 'bool', label: 'Display-health detector', description: 'Report when the Parsec display is stuck at a non-streamable resolution.' },
      { section: 'display_health', key: 'poll_seconds', type: 'int', label: 'Poll (s)', description: 'Poll cadence. Default 15.' },
      { section: 'display_health', key: 'miss_threshold', type: 'int', label: 'Miss threshold', description: 'Consecutive suspect polls before flagging. Default 2.' },
      { section: 'display_health', key: 'relog_minutes', type: 'int', label: 'Re-log (min)', description: 'Re-log a still-stuck display every N minutes. Default 10.' },
    ],
  },
  {
    section: 'time_sync',
    title: 'Clock skew',
    knobs: [
      { section: 'time_sync', key: 'enabled', type: 'bool', label: 'Clock-skew auto-correct', description: 'Auto-correct system clock skew (w32tm resync, then direct set).' },
      { section: 'time_sync', key: 'threshold_seconds', type: 'int', label: 'Threshold (s)', description: 'Minimum |skew| before correcting. Default 120.' },
      { section: 'time_sync', key: 'cooldown_seconds', type: 'int', label: 'Cooldown (s)', description: 'Minimum gap between correction attempts. Default 600.' },
    ],
  },
  {
    section: 'crash_recovery',
    title: 'Crash recovery',
    knobs: [
      { section: 'crash_recovery', key: 'auto_restart', type: 'bool', label: 'Auto-restart', description: 'Automatically relaunch the same experience on crash.' },
      { section: 'crash_recovery', key: 'max_restarts', type: 'int', label: 'Max auto-restarts', description: 'Auto-restarts allowed within the window before showing the persistent error. Default 1.' },
      { section: 'crash_recovery', key: 'upload_logs', type: 'bool', label: 'Upload crash logs', description: 'Upload crash-log zips to Supabase Storage.' },
    ],
  },
  {
    section: 'incidents',
    title: 'Incidents',
    knobs: [
      { section: 'incidents', key: 'enabled', type: 'bool', label: 'Incident capture', description: 'Capture incidents (manual + freeze + crash mirror) to Storage.' },
      { section: 'incidents', key: 'screenshot', type: 'bool', label: 'Incident screenshot', description: 'Capture a PNG of the running experience window into the bundle.' },
    ],
  },
  {
    section: 'visitor_messages',
    title: 'Visitor messages',
    knobs: [
      { section: 'visitor_messages', key: 'enabled', type: 'bool', label: 'Visitor voice messages', description: 'Play operator warning clips to the visitor via the PC default output (heard in the headset).' },
      { section: 'visitor_messages', key: 'headset_dialog', type: 'bool', label: 'Bericht ook als headset-dialoog', description: 'Show the visitor message as a PICO dialog on the paired headset (shell 1.0.10+), with or without a running build. Default on.' },
      { section: 'visitor_messages', key: 'headset_dialog_seconds', type: 'int', label: 'Duur headset-dialoog (s)', description: 'How long the headset dialog stays visible, 1 to 100 seconds. Default 8.' },
      { section: 'visitor_messages', key: 'headset_dialog_title', type: 'string', label: 'Titel headset-dialoog', description: 'Title of the headset dialog. Default Bericht.' },
      { section: 'visitor_messages', key: 'headset_dialog_icon_url', type: 'string', label: 'Icoon headset-dialoog (URL)', description: 'Optional http URL of a PNG/JPG on the room LAN shown as the dialog icon (logo-sized).' },
      { section: 'visitor_messages', key: 'duck_level', type: 'int', label: 'Message duck volume', description: 'Master volume while a warning clip plays. The clip is attenuated too — keep >=30. Default 40.' },
    ],
  },
  {
    section: 'subsystems',
    title: 'Subsystem switches',
    knobs: [
      { section: 'convai', key: 'enabled', type: 'bool', label: 'NGRConvai management', description: 'Enable NGRConvai status/control/reconcile.' },
      { section: 'convai', key: 'native', type: 'bool', label: 'NGRConvai native mode', description: 'Run the native orchestrator instead of proxying NGRConvAI.exe.', danger: true },
      { section: 'owned_apps', key: 'enabled', type: 'bool', label: 'Owned-apps management', description: 'Enable owned-apps monitoring and auto-restart.' },
      { section: 'pico_service', key: 'enabled', type: 'bool', label: 'PICO device service', description: 'Supervise PicoDeviceService.exe (headset status). Disabling breaks HMD status.', danger: true },
      { section: 'dedicated_servers', key: 'enabled', type: 'bool', label: 'Dedicated servers', description: 'Enable dedicated-server management on this PC.' },
    ],
  },
  {
    section: 'diagnostics',
    title: 'Diagnostics',
    knobs: [
      { section: 'diagnostics', key: 'boot_test_experience_id', type: 'string', label: 'Boot-test experience', description: 'Experience ID the boot test launches. Pin to the VR probe build; empty = newest installed release.' },
    ],
  },
  {
    section: 'logging',
    title: 'Logging',
    knobs: [
      { section: 'logging', key: 'level', type: 'string', label: 'Log level', description: 'Verbosity of the agent log.', options: ['debug', 'info', 'warn', 'error'] },
    ],
  },
];

// Flat list of every knob, for lookups / summaries.
export const ALL_KNOBS: Knob[] = AGENT_CONFIG_SCHEMA.flatMap((s) => s.knobs);
